/**
 * StatsAcntgYrUseAmtInq.tsx
 * ------------------------------------------------------------
 * 관항목별 예산-사용금액 비교 통계
 */

import React from "react";
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Util from "@/js/common/Util";
import SelectBox from "@/js/common/ui/input/SelectBox";
import Button from "@/js/common/ui/Button";
import * as Atom from "@/js/common/Atom";
import { useRecoilValue } from "recoil";
import { exportToXlsx } from "@/js/common/ExcelExport";
import { fldInfsProps } from "@/js/common/ui/GridTable";

interface Props {
    systemCode: string;
}

type RowType = {
    bgtSbjct1stNm: string;   // 관
    bgtSbjct2stNm: string;   // 항
    bgtSbjct3stNm: string;   // 목
    frmtAmt: string;         // 예산액
    implAmt: string;         // 집행누계
    monthImplAmt: string;    // 월 집행액
    balAmt: string;          // 잔액
    useRt: number;           // 사용률
};

const StatsAcntgYrUseAmtInq = ({ systemCode }: Props) => {
    const userInfo = useRecoilValue(Atom.userInfo);

    // =========================
    // 검색 조건
    // =========================
    const [acctYear, setAcctYear] = React.useState<string>(Util.getNowAcntgYear());
    const [acntgMonth, setAcntgMonth] = React.useState<string>(
        String(new Date().getMonth() + 1).padStart(2, "0")
    );

    // 관/항/목
    const [kwanCd, setKwanCd] = React.useState<string>("");
    const [hangCd, setHangCd] = React.useState<string>("");
    const [mokCd, setMokCd] = React.useState<string>("");

    // 본부/지부 값 고정(표시만)
    const upOgnzCd = String(userInfo.upOgnzCd);
    const ognzCd = String(userInfo.ognzCd);

    // =========================
    // 데이터
    // =========================
    const [rows, setRows] = React.useState<RowType[]>([]);
    const [totalFrmtAmt, setTotalFrmtAmt] = React.useState<string>("0");
    const [totalImplAmt, setTotalImplAmt] = React.useState<string>("0");
    const [totalMonthImplAmt, setTotalMonthImplAmt] = React.useState<string>("0");
    const [totalBalAmt, setTotalBalAmt] = React.useState<string>("0");
    const [totalUseRt, setTotalUseRt] = React.useState<number>(0);
    const [dataArr, setDataArr] = React.useState<any[]>([]);
    const [systemName, setSystemName] = React.useState<string>();

    // =========================
    // helpers
    // =========================
    const monthNum = Number(acntgMonth);
    const displayYear = (monthNum === 1 || monthNum === 2) ? String(Number(acctYear) + 1) : acctYear;
    const displayMonth = acntgMonth ? String(monthNum) : "전체";

    const toNum = (v: any) => {
        if (v === null || v === undefined || v === "") return 0;

        const n = Number(Util.uncomma(String(v)));
        return Number.isFinite(n) ? n : 0;
    };

    const getBarColor = (rt: number) => {
        const clamped = Math.max(0, Math.min(100, Number(rt) || 0));
        return clamped >= 100 ? "#007bff" : clamped >= 80 ? "#27AE60" : "#ffc107";
    };

    const isSpcFund = ["C1", "C2", "C3", "C4", "C5"].includes(systemCode);

    // 관
    const kwanCodeArray = isSpcFund
        ? [{ code: "", name: "전체" }].concat(Util.getSpcBgt1CodeArray(systemCode, "02"))
        : [{ code: "", name: "전체" }].concat(Util.getBgt1CodeArray("02", ognzCd));

    // 항
    const hangCodeArray = isSpcFund
        ? [{ code: "", name: "전체" }].concat(Util.getSpcBgt2CodeArray(systemCode, kwanCd))
        : [{ code: "", name: "전체" }].concat(Util.getBgt2CodeArray(kwanCd, ognzCd));

    // 목
    const mokCodeArray = isSpcFund
        ? [{ code: "", name: "전체" }].concat(Util.getSpcBgt3CodeArray(systemCode, hangCd))
        : [{ code: "", name: "전체" }].concat(Util.getBgt3CodeArray(hangCd, ognzCd));

    React.useEffect(() => {
        setKwanCd("");
        setHangCd("");
        setMokCd("");
    }, [systemCode]);

    // =========================
    // 초기화 버튼
    // =========================
    const resetFilters = () => {
        setAcctYear(Util.getNowAcntgYear());
        setAcntgMonth("");
        setKwanCd("");
        setHangCd("");
        setMokCd("");
    };

    // =========================
    // 조회
    // =========================
    React.useEffect(() => {
        AppContext.showSpinner();

        Axios.dataAccess({
            url: "system/stats/select-stats-acntg-yr-use-amt.do",
            methodType: "post",
            paramType: "json",
            paramData: {
                acntgYear: acctYear,
                bgtSeCd: systemCode,
                upOgnzCd: upOgnzCd,
                ognzCd: ognzCd,
                acntgMonth: acntgMonth,
                bgtSbjct1stCd: kwanCd,
                bgtSbjct2stCd: hangCd,
                bgtSbjct3stCd: mokCd,
            },

            onSuccessFn: (res: any) => {
                const list = res?.result?.list ?? [];
                const sum = res?.result?.listSum ?? {};

                const mapped: RowType[] = (Array.isArray(list) ? list : []).map((it: any) => {
                    const frmtAmtNum = toNum(it.frmtAmt);
                    const implAmtNum = toNum(it.implAmt);
                    const monthImplAmtNum = toNum(it.monthImplAmt);
                    const balAmtNum = toNum(it.balAmt);
                    const useRtNum = it.useRt;

                    return {
                        bgtSbjct1stNm: String(it.bgtSbjct1stNm ?? ""),
                        bgtSbjct2stNm: String(it.bgtSbjct2stNm ?? ""),
                        bgtSbjct3stNm: String(it.bgtSbjct3stNm ?? ""),
                        frmtAmt: Util.comma(String(frmtAmtNum)),
                        implAmt: Util.comma(String(implAmtNum)),
                        monthImplAmt: Util.comma(String(monthImplAmtNum)),
                        balAmt: Util.comma(String(balAmtNum)),
                        useRt: useRtNum,
                    };
                });

                const excelRows = (Array.isArray(list) ? list : []).map((it: any) => {
                    const frmtAmtNum = toNum(it.frmtAmt);
                    const implAmtNum = toNum(it.implAmt);
                    const monthImplAmtNum = toNum(it.monthImplAmt);
                    const balAmtNum = toNum(it.balAmt);
                    const useRtNum = Math.max(0, Math.min(100, Number(it.useRt) || 0));

                    return {
                        ...it,
                        frmtAmt: frmtAmtNum,
                        implAmt: implAmtNum,
                        monthImplAmt: monthImplAmtNum,
                        balAmt: balAmtNum,
                        useRt: useRtNum,
                    };
                });

                const sumFrmtNum = toNum(sum?.frmtAmt);
                const sumImplNum = toNum(sum?.implAmt);
                const sumMonthImplNum = toNum(sum?.monthImplAmt);
                const sumBalNum = toNum(sum?.balAmt);
                const sumRt = toNum(sum?.useRt);

                setDataArr(excelRows);
                setRows(mapped);
                setTotalFrmtAmt(Util.comma(String(sumFrmtNum)));
                setTotalImplAmt(Util.comma(String(sumImplNum)));
                setTotalMonthImplAmt(Util.comma(String(sumMonthImplNum)));
                setTotalBalAmt(Util.comma(String(sumBalNum)));
                setTotalUseRt(sumRt);

                AppContext.hideSpinner();
            },
        });
    }, [acctYear, acntgMonth, kwanCd, hangCd, mokCd, upOgnzCd, ognzCd, systemCode]);

    // 특별회계기금 타이틀 설정
    React.useEffect(() => {
        if (systemCode === "C1") {
            setSystemName("복지기금");
        } else if (systemCode === "C2") {
            setSystemName("자립기금");
        } else if (systemCode === "C3") {
            setSystemName("발전기금");
        } else if (systemCode === "C4") {
            setSystemName("희생자부조금");
        } else if (systemCode === "C5") {
            setSystemName("퇴직급여충당금");
        } else setSystemName("")
    }, [systemCode]);

    const fldInfs: fldInfsProps[] = [
        { type: "text",  fldNm: "bgtSbjct1stNm", width: 120, headAlign: "center", dataAlign: "left",   label: "관" },
        { type: "text",  fldNm: "bgtSbjct2stNm", width: 140, headAlign: "center", dataAlign: "left",   label: "항" },
        { type: "text",  fldNm: "bgtSbjct3stNm", width: 180, headAlign: "center", dataAlign: "left",   label: "목" },
        { type: "comma", fldNm: "frmtAmt",       width: 140, headAlign: "center", dataAlign: "right",  label: "예산액" },
        { type: "comma", fldNm: "implAmt",       width: 140, headAlign: "center", dataAlign: "right",  label: "집행누계" },
        { type: "comma", fldNm: "monthImplAmt",  width: 140, headAlign: "center", dataAlign: "right",  label: "월 집행액" },
        { type: "comma", fldNm: "balAmt",        width: 140, headAlign: "center", dataAlign: "right",  label: "잔액" },
        { type: "text",  fldNm: "useRt",         width: 100, headAlign: "center", dataAlign: "center", label: "사용률(%)" },
    ];

    function downloadExcel() {
        if (dataArr.length === 0) {
            AppContext.openAlert({
                title: "데이터 확인",
                msg: "엑셀로 다운로드할 데이터가 없습니다.",
                icon: "warning",
            });
            return;
        }

        // 합계 행 추가
        const totalRow = {
            bgtSbjct1stNm: "총 합계",
            bgtSbjct2stNm: "",
            bgtSbjct3stNm: "",
            frmtAmt: Number(Util.uncomma(totalFrmtAmt || "0")),
            implAmt: Number(Util.uncomma(totalImplAmt || "0")),
            monthImplAmt: Number(Util.uncomma(totalMonthImplAmt || "0")),
            balAmt: Number(Util.uncomma(totalBalAmt || "0")),
            useRt: Math.max(0, Math.min(100, Number(totalUseRt) || 0)).toFixed(2),
            excelRowStyle : {
                fillColor: "FFEF99",
                fontColor: "000000",
                bold: true,
            },
        };

        const excelRows = [
            ...dataArr,
            totalRow,
        ];

        exportToXlsx({
            fileName: `${displayYear}년 ${acntgMonth ? `${displayMonth}월 ` : ""} ${systemName} 관항목별 예산-사용금액 비교 통계`,
            title: `${displayYear}년 ${acntgMonth ? `${displayMonth}월 ` : ""} ${systemName} 관항목별 예산-사용금액 비교 통계`,
            subTitleLeft: `${displayYear}회계년도`,
            subTitleRight: "(금액단위:원)",
            columns: fldInfs,
            rows: excelRows,
        });
    }

    return (
        <div className="w1200 bgcWhite p20 bdrd8 mt10 boxShadowSoft">
            {/* 타이틀 */}
            <div className="mt5">
                <span className="dpBlock w100p fm-c fs25 fc2c3e50 ls1 pb10">
                    관,항,목별 집행현황 (시산표)
                </span>
                <div className="bdt3 bdc007bff pb10" />
            </div>

            {/* 검색 조건 */}
            <div
                className="bd1 bdcDDDDDD p20 mb10 bdrd6 bgcFAFAFA"
                style={{
                    position: "relative",
                    zIndex: 10,
                    overflow: "visible",
                }}
            >
                {/* 1줄 */}
                <div className="dpFlx aiC flxWrap gap14 mb10">
                    {/* 본부 */}
                    <div className="dpFlx aiC">
                        <span className="w40 fm-c fs15 fc333333 ls1 taL">본부</span>
                        <SelectBox
                            value={upOgnzCd}
                            width={140}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            background="#F6F6F6"
                            codeArray={Util.getUpOrgCodeArray("01")}
                            placeholder="전체"
                            paddingRight={8}
                            disabled={true}
                            onChangeFunc={() => {}}
                        />
                    </div>

                    {/* 지부 */}
                    <div className="dpFlx aiC">
                        <span className="w40 fm-c fs15 fc333333 ls1 taL">지부</span>
                        <SelectBox
                            value={ognzCd}
                            width={150}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            background="#F6F6F6"
                            codeArray={Util.getDownOrgCodeArray(upOgnzCd)}
                            placeholder="전체"
                            paddingRight={8}
                            disabled={true}
                            onChangeFunc={() => {}}
                        />
                    </div>

                    {/* 회계연도 */}
                    <div className="dpFlx aiC">
                        <span className="w70 fm-c fs15 fc333333 ls1 taL">회계연도</span>
                        <SelectBox
                            value={acctYear}
                            width={100}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            background="#fff"
                            codeArray={Util.getAcntgYearArray()}
                            placeholder="선택"
                            paddingRight={8}
                            onChangeFunc={(v: string) => setAcctYear(v)}
                        />
                    </div>

                    {/* 월별 조회 */}
                    <div className="dpFlx aiC">
                        <span className="w80 fm-c fs15 fc333333 ls1 taL">월별 조회</span>
                        <SelectBox
                            value={acntgMonth}
                            width={140}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            background="#fff"
                            paddingRight={8}
                            codeArray={[{ code: "", name: "전체" }].concat(Util.getCodeArray("SRCH_MM"))}
                            placeholder="전체"
                            onChangeFunc={(data: string) => setAcntgMonth(data)}
                        />
                    </div>
                </div>

                {/* 2줄 */}
                <div className="dpFlx aiC flxWrap gap14 jcSb">
                    <div className="dpFlx aiC flxWrap gap14">
                        {/* 관 */}
                        <div className="dpFlx aiC">
                            <span className="w40 fm-c fs15 fc333333 ls1 taL">관</span>
                            <SelectBox
                                value={kwanCd}
                                width={160}
                                height={36}
                                fontSize={14}
                                fontFamily="fm-c"
                                color="#333"
                                letterSpacing={-1}
                                borderColor="#ddd"
                                borderOnColor="#222"
                                borderRadius={2}
                                background="#fff"
                                codeArray={kwanCodeArray}
                                placeholder="선택"
                                paddingRight={8}
                                onChangeFunc={(v: string) => {
                                    setKwanCd(v);
                                    setHangCd("");
                                    setMokCd("");
                                }}
                            />
                        </div>

                        {/* 항 */}
                        <div className="dpFlx aiC">
                            <span className="w40 fm-c fs15 fc333333 ls1 taL">항</span>
                            <SelectBox
                                value={hangCd}
                                width={180}
                                height={36}
                                fontSize={14}
                                fontFamily="fm-c"
                                color="#333"
                                letterSpacing={-1}
                                borderColor="#ddd"
                                borderOnColor="#222"
                                borderRadius={2}
                                background="#fff"
                                codeArray={hangCodeArray}
                                placeholder="선택"
                                paddingRight={8}
                                onChangeFunc={(v: string) => {
                                    setHangCd(v);
                                    setMokCd("");
                                }}
                            />
                        </div>

                        {/* 목 */}
                        <div className="dpFlx aiC">
                            <span className="w40 fm-c fs15 fc333333 ls1 taL">목</span>
                            <SelectBox
                                value={mokCd}
                                width={220}
                                height={36}
                                fontSize={14}
                                fontFamily="fm-c"
                                color="#333"
                                letterSpacing={-1}
                                borderColor="#ddd"
                                borderOnColor="#222"
                                borderRadius={2}
                                background="#fff"
                                codeArray={mokCodeArray}
                                placeholder="선택"
                                paddingRight={8}
                                onChangeFunc={(v: string) => setMokCd(v)}
                            />
                        </div>
                    </div>

                    {/* 버튼 */}
                    <div className="dpFlx aiC gap12">
                        <Button
                            width={150}
                            height={40}
                            fontFamily="fm-b"
                            fontSize={16}
                            letterSpacing={-1}
                            variant="styExcelBtn"
                            borderRadius={6}
                            buttonClass="dpFlx aiC jcC"
                            buttonName="Excel 다운로드"
                            onClickFunc={downloadExcel}
                        />

                        <Button
                            width={90}
                            height={40}
                            fontFamily="fm-b"
                            fontSize={16}
                            letterSpacing={-1}
                            variant="styInitBtn"
                            borderRadius={6}
                            buttonClass="dpFlx aiC jcC"
                            buttonName="초기화"
                            onClickFunc={resetFilters}
                        />
                    </div>
                </div>
            </div>

            {/* 테이블 영역 */}
            <div className="w100p bsBdb bgcFDFCFC">
                <div
                    className="w100p"
                    style={{
                        maxHeight: 540,
                        overflowY: "auto",
                        overflowX: "auto",
                        borderTop: "1px solid #C5C5C5",
                        borderLeft: "1px solid #DDDDDD",
                        borderRight: "1px solid #DDDDDD",
                        borderBottom: "1px solid #DDDDDD",
                        borderRadius: 8,
                        position: "relative",
                        zIndex: 0,
                    }}
                >
                    <table
                        className="w100p tlF"
                        style={{
                            borderCollapse: "collapse",
                            borderSpacing: 0,
                        }}
                    >
                        <colgroup>
                            <col style={{ width: 120 }} />
                            <col style={{ width: 130 }} />
                            <col style={{ width: 180 }} />
                            <col style={{ width: 130 }} />
                            <col style={{ width: 130 }} />
                            <col style={{ width: 130 }} />
                            <col style={{ width: 130 }} />
                            <col style={{ width: 90 }} />
                            <col style={{ width: 150 }} />
                        </colgroup>

                        <thead
                            style={{
                                position: "sticky",
                                top: 0,
                                zIndex: 5,
                                background: "#EFF8FB",
                            }}
                        >
                            <tr>
                                <th
                                    colSpan={3}
                                    className="bd1 bdcDDDDDD taC fm-b fs14 fc333333"
                                    style={{
                                        padding: "10px 12px",
                                        lineHeight: "18px",
                                        verticalAlign: "middle",
                                        background: "#EFF8FB",
                                        borderColor: "#DDDDDD",
                                        backgroundClip: "padding-box",
                                    }}
                                >
                                    관항목 상세
                                </th>
                                <th
                                    colSpan={6}
                                    className="bd1 bdcDDDDDD taC fm-b fs14 fc333333"
                                    style={{
                                        padding: "10px 12px",
                                        lineHeight: "18px",
                                        verticalAlign: "middle",
                                        background: "#EFF8FB",
                                        borderColor: "#DDDDDD",
                                        backgroundClip: "padding-box",
                                    }}
                                >
                                    예산 및 사용 현황
                                </th>
                            </tr>

                            <tr>
                                {["관", "항", "목", "예산액 (원)", "집행누계 (원)", "월 집행액 (원)", "잔액 (원)", "사용률 (%)", "사용 현황"].map(
                                    (label) => (
                                        <th
                                            key={label}
                                            className="bd1 bdcDDDDDD taC fm-b fs14 fc333333"
                                            style={{
                                                padding: "10px 12px",
                                                lineHeight: "18px",
                                                verticalAlign: "middle",
                                                background: "#EFF8FB",
                                                borderColor: "#DDDDDD",
                                                backgroundClip: "padding-box",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {label}
                                        </th>
                                    )
                                )}
                            </tr>
                        </thead>

                        <tbody>
                            {rows.length === 0 ? (
                                <tr>
                                    <td colSpan={9} className="bd1 bdcDDDDDD p16 taC fm-c fs14 fc666666">
                                        조회 결과가 없습니다.
                                    </td>
                                </tr>
                            ) : (
                                rows.map((r, idx) => {
                                    const rtClamped = Math.max(0, Math.min(100, Number(r.useRt) || 0));
                                    const barW = `${rtClamped}%`;
                                    const barColor = getBarColor(rtClamped);

                                    return (
                                        <tr
                                            key={`${r.bgtSbjct1stNm}-${r.bgtSbjct2stNm}-${r.bgtSbjct3stNm}-${idx}`}
                                            className="bgcWhite"
                                        >
                                            <td className="bd1 bdcDDDDDD" style={{ padding: "10px 12px" }}>
                                                <div className="fm-c fs14 fc333333 ls06 wsNw ofH toE">{r.bgtSbjct1stNm}</div>
                                            </td>
                                            <td className="bd1 bdcDDDDDD" style={{ padding: "10px 12px" }}>
                                                <div className="fm-c fs14 fc333333 ls06 wsNw ofH toE">{r.bgtSbjct2stNm}</div>
                                            </td>
                                            <td className="bd1 bdcDDDDDD" style={{ padding: "10px 12px" }}>
                                                <div className="fm-c fs14 fc333333 ls06 wsNw ofH toE">{r.bgtSbjct3stNm}</div>
                                            </td>

                                            <td className="bd1 bdcDDDDDD" style={{ padding: "10px 12px" }}>
                                                <div className="fm-c fs14 fw700 fc333333 taR">{r.frmtAmt}</div>
                                            </td>

                                            <td className="bd1 bdcDDDDDD" style={{ padding: "10px 12px" }}>
                                                <div className="fm-c fs14 fw700 fc333333 taR">{r.implAmt}</div>
                                            </td>

                                            <td className="bd1 bdcDDDDDD" style={{ padding: "10px 12px" }}>
                                                <div className="fm-c fs14 fw700 fc333333 taR">{r.monthImplAmt}</div>
                                            </td>

                                            <td className="bd1 bdcDDDDDD" style={{ padding: "10px 12px" }}>
                                                <div className="fm-c fs14 fw700 fc333333 taR">{r.balAmt}</div>
                                            </td>

                                            <td className="bd1 bdcDDDDDD" style={{ padding: "10px 12px" }}>
                                                <div
                                                    className="fm-c fs14 fw700 taC"
                                                    style={{
                                                        color: rtClamped >= 100 ? "#007bff" : rtClamped >= 80 ? "#27AE60" : "#d19d0d",
                                                    }}
                                                >
                                                    {rtClamped.toFixed(2)}
                                                </div>
                                            </td>

                                            <td className="bd1 bdcDDDDDD" style={{ padding: "10px 12px" }}>
                                                <div
                                                    className="w100p"
                                                    style={{
                                                        height: 18,
                                                        background: "#F1F1F1",
                                                        borderRadius: 6,
                                                        overflow: "hidden",
                                                        position: "relative",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            height: "100%",
                                                            width: barW,
                                                            background: barColor,
                                                            transition: "width 0.2s ease",
                                                        }}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>

                        <tfoot>
                            <tr
                                className="bgcE1E1E1"
                                style={{
                                    position: "sticky",
                                    bottom: 0,
                                    zIndex: 4,
                                }}
                            >
                                <td colSpan={3} className="bd1 bdcDDDDDD" style={{ padding: "10px 12px" }}>
                                    <div className="taC fm-c fs14 fc333333 fw700">총 합계</div>
                                </td>

                                <td className="bd1 bdcDDDDDD" style={{ padding: "10px 12px" }}>
                                    <div className="taR fm-c fs14 fcBlack fw700">{totalFrmtAmt}</div>
                                </td>

                                <td className="bd1 bdcDDDDDD" style={{ padding: "10px 12px" }}>
                                    <div className="taR fm-c fs14 fcBlack fw700">{totalImplAmt}</div>
                                </td>

                                <td className="bd1 bdcDDDDDD" style={{ padding: "10px 12px" }}>
                                    <div className="taR fm-c fs14 fcBlack fw700">{totalMonthImplAmt}</div>
                                </td>

                                <td className="bd1 bdcDDDDDD" style={{ padding: "10px 12px" }}>
                                    <div className="taR fm-c fs14 fcBlack fw700">{totalBalAmt}</div>
                                </td>

                                <td className="bd1 bdcDDDDDD" style={{ padding: "10px 12px" }}>
                                    <div className="taC fm-c fs14 fcBlack fw700">
                                        {Math.max(0, Math.min(100, Number(totalUseRt) || 0)).toFixed(2)}
                                    </div>
                                </td>

                                <td className="bd1 bdcDDDDDD" style={{ padding: "10px 12px" }}>
                                    {(() => {
                                        const rt = Math.max(0, Math.min(100, Number(totalUseRt) || 0));
                                        const color = getBarColor(rt);

                                        return (
                                            <div
                                                className="w100p"
                                                style={{
                                                    height: 18,
                                                    background: "#F1F1F1",
                                                    borderRadius: 6,
                                                    overflow: "hidden",
                                                }}
                                            >
                                                <div style={{ height: "100%", width: `${rt}%`, background: color }} />
                                            </div>
                                        );
                                    })()}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default StatsAcntgYrUseAmtInq;